/**
 * Gemeinsame Hunde-Typen.
 *
 * Entspricht der Antwort von `GET /api/members/all_dogs_status`
 * (bzw. `GET /api/dogs/status/:dogId`) und wird von der Hundeverwaltung,
 * der DogDetailCard und dem DogDetailModal geteilt.
 */

export interface DogDocument {
  docId: number;
  filename: string;
  created_at: string;
}

export interface Dog {
  dog_id: number;
  hundename: string;
  vollstaendiger_name?: string | null;
  rasse?: string | null;
  geburtstag?: string | null;
  chipnummer?: string | null;
  tassonummer?: string | null;
  geschlecht?: string | number | null;
  kastriert: boolean | number | string;
  image_path?: string | null;
  image_path_updated?: string | null;
  hundebox?: string | number | null;
  haftpflicht?: string | null;
  insurance_name?: string | null;
  valid_insurance: boolean;
  insurance_checked_at?: string | null;
  insurance_checked_by?: string | null;
  valid_tollwut: boolean;
  tollwut_checked_at?: string | null;
  tollwut_checked_by?: string | null;
  tollwut_end_date?: string | null;
  documents: {
    insurance: DogDocument[];
    tollwut: DogDocument[];
  };
}

export type DogDocumentCategory = 'insurance' | 'tollwut';

/** Dokumenttyp-IDs aus `dog_document_type` (1 = Tollwut, 2 = Haftpflicht). */
export const DOG_DOC_TYPE_ID: Record<DogDocumentCategory, number> = {
  tollwut: 1,
  insurance: 2,
};

/** Alter als Text („3 Jahre"), robust gegen fehlendes/ungültiges Datum. */
export function dogAgeLabel(geburtstag?: string | null): string {
  if (!geburtstag) return 'Unbekannt';
  const d = new Date(geburtstag);
  if (Number.isNaN(d.getTime())) return 'Unbekannt';
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years--;
  if (years < 0) return 'Unbekannt';
  if (years === 0) {
    const months = Math.max(
      0,
      (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()),
    );
    return `${months} Monat${months === 1 ? '' : 'e'}`;
  }
  return `${years} Jahr${years === 1 ? '' : 'e'}`;
}

/** Ja/Nein/Unbekannt aus den heterogenen DB-Werten (0/1, 'ja'/'nein', boolean). */
export function dogFlagLabel(v: boolean | number | string | null | undefined): string {
  if (v === true || v === 1 || v === '1') return 'Ja';
  if (v === false || v === 0 || v === '0') return 'Nein';
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'ja' || s === 'true') return 'Ja';
    if (s === 'nein' || s === 'false') return 'Nein';
    if (s) return v;
  }
  return 'Unbekannt';
}

/** Tollwutimpfung abgelaufen? */
export function isTollwutExpired(dog: Pick<Dog, 'tollwut_end_date'>): boolean {
  if (!dog.tollwut_end_date) return false;
  const d = new Date(dog.tollwut_end_date);
  return !Number.isNaN(d.getTime()) && d < new Date();
}

/** Hat der Hund offene Pflichtnachweise? */
export function dogHasIssues(dog: Dog): boolean {
  const insuranceOk = dog.valid_insurance && dog.documents.insurance.length > 0;
  const tollwutOk =
    dog.valid_tollwut && dog.documents.tollwut.length > 0 && !isTollwutExpired(dog);
  return !insuranceOk || !tollwutOk;
}

/** Halter/Mitglied ist reiner Gast (nur Rolle `guest`). */
export function isGuestOnly(roles?: string[] | null): boolean {
  return !!roles && roles.length > 0 && roles.every((r) => r === 'guest');
}
